import { css } from "styled-system/css";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { useAuth } from "@/providers/AuthProvider";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export function AcceptInvitePage() {
  const { user, loading, refresh } = useAuth();
  const [sp] = useSearchParams();
  const token = sp.get("token");

  const [state, setState] = useState<"loading" | "ok" | "login" | "error">("loading");
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setState("error");
      setMsg("Token de invitación inválido.");
      return;
    }
    if (loading) return;
    if (!user) {
      setState("login");
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const r = await fetch("/api/organizations/accept-invite", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ token }),
        });
        if (cancelled) return;
        if (!r.ok) {
          const j = (await r.json().catch(() => ({}))) as { error?: string };
          setState("error");
          setMsg(j.error ?? "No se pudo aceptar la invitación.");
          return;
        }
        await refresh();
        setState("ok");
      } catch {
        if (cancelled) return;
        setState("error");
        setMsg("Error al aceptar la invitación. Probá de nuevo.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, loading, user, refresh]);

  const loginCallback = token ? `/auth/accept-invite?token=${encodeURIComponent(token)}` : "/auth/accept-invite";

  return (
    <div className={css({ minH: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: "6", bg: "bg.muted" })}>
      <Card className={css({ maxW: "520px", width: "100%", textAlign: "center", p: "6" })}>
        {state === "loading" ? (
          <>
            <LoadingFrame variant="light" minimal minHeight={96} />
            <p className={css({ mt: "4", fontSize: "sm", color: "#64748b" })}>Validando invitación…</p>
          </>
        ) : null}

        {state === "login" ? (
          <>
            <h1 className={css({ fontSize: "2xl", fontWeight: "bold", mb: "2", color: "fg.DEFAULT" })}>Iniciá sesión</h1>
            <p className={css({ fontSize: "sm", color: "#64748b", mb: "6" })}>
              Necesitamos tu cuenta para aceptar esta invitación.
            </p>
            <Link to={`/auth/login?callbackUrl=${encodeURIComponent(loginCallback)}`}>
              <Button variant="primary">Ir a login</Button>
            </Link>
          </>
        ) : null}

        {state === "ok" ? (
          <>
            <h1 className={css({ fontSize: "2xl", fontWeight: "bold", mb: "2", color: "fg.DEFAULT" })}>¡Listo!</h1>
            <p className={css({ fontSize: "sm", color: "#64748b", mb: "6" })}>
              Ya estás asociado/a al equipo. Podés verlo en tu panel.
            </p>
            <Link to="/dashboard/team">
              <Button variant="secondary">Ir a Mi equipo</Button>
            </Link>
          </>
        ) : null}

        {state === "error" ? (
          <>
            <h1 className={css({ fontSize: "2xl", fontWeight: "bold", mb: "2", color: "fg.DEFAULT" })}>No se pudo aceptar</h1>
            <p className={css({ fontSize: "sm", color: "#b91c1c", mb: "6" })}>{msg ?? "Error"}</p>
            <Link to="/dashboard">
              <Button variant="secondary">Volver al panel</Button>
            </Link>
          </>
        ) : null}
      </Card>
    </div>
  );
}

